import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'

function token(): string | null {
  return localStorage.getItem('mf_access')
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`
  return headers
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // non-JSON error response
  }
  const errBody =
    (body as { error?: { code: string; message: string; details?: Record<string, unknown> } } | null)
      ?.error ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, errBody)
}

export interface OrderAcceptanceSettings {
  pickupEnabled: boolean
  deliveryEnabled: boolean
}

export interface SettingsOrganization {
  id: string
  name: string
  slug: string
  description: string | null
  logoUrl: string | null
  coverUrl: string | null
  locale: string
  timezone: string
  currency: string
  pricePrecisionMode: string
  status: string
}

export interface SettingsLocation {
  id: string
  name: string
  slug: string | null
  description: string | null
  address: string | null
  phone: string | null
  email: string | null
  timezone: string
  currency: string
  locale: string
  status: string
  orderAcceptance: OrderAcceptanceSettings
}

export interface SettingsLocationSummary {
  id: string
  name: string
  slug: string | null
  status: string
}

export interface ScheduleEntry {
  id?: string
  dayOfWeek: number
  openTime: string
  closeTime: string
  isActive?: boolean
}

export interface OrderScheduleSettings {
  isConfigured: boolean
  timezone: string
  entries: ScheduleEntry[]
}

export interface SettingsResponse {
  organization: SettingsOrganization
  locations: SettingsLocationSummary[]
  location: SettingsLocation | null
  orderSchedule: OrderScheduleSettings | null
}

export interface UpdateSettingsData {
  locationId?: string
  organization?: {
    name?: string
    slug?: string
    description?: string | null
    logoUrl?: string | null
    coverUrl?: string | null
    locale?: string
    timezone?: string
    pricePrecisionMode?: string
  }
  location?: {
    name?: string
    description?: string | null
    address?: string | null
    phone?: string | null
    email?: string | null
    timezone?: string
  }
  orderAcceptance?: Partial<OrderAcceptanceSettings>
}

export async function getSettings(organizationId: string, locationId?: string): Promise<SettingsResponse> {
  const query = locationId ? `?locationId=${encodeURIComponent(locationId)}` : ''
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/settings${query}`, {
    headers: authHeaders(),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to load settings')
  const body = await res.json()
  return body.data
}

export async function updateSettings(
  organizationId: string,
  data: UpdateSettingsData,
): Promise<SettingsResponse> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/settings`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update settings')
  const body = await res.json()
  return body.data
}

export async function updateOrderSchedule(
  organizationId: string,
  data: {
    locationId?: string
    configured: boolean
    entries: { dayOfWeek: number; openTime: string; closeTime: string }[]
  },
): Promise<OrderScheduleSettings> {
  const res = await fetch(`${API_BASE}/organizations/${organizationId}/settings/order-schedule`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update order schedule')
  const body = await res.json()
  return body.data
}
